import { ThemeProvider, Box, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { createContext, ForwardedRef, forwardRef, Suspense, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import tokenService from "../../services/tokenservice";
import { ConfirmacaoProvider } from "../confirmacao/confirmacao";
import "./browser.css";
import PageRender from "./pagerender/pagerender";
import PageBody from "./taskbar/PageBody/pagebody";
import TaskBar from "./taskbar/taskbar";
import { BrowserContextType, MenuItemType } from "./taskbar/taskbartypes.d";
import { criarTemaCustomizado, NomeTema } from "./taskbar/tema";

dayjs.locale('pt-br');
dayjs.extend(customParseFormat);

const EasyBrowserContext = createContext<BrowserContextType | undefined>(undefined);
export const useEasyBrowserContext = () => useContext(EasyBrowserContext);

const Browser = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const pagainaDashboard: MenuItemType = {
        id: 'easy.schedule.menu.principal',
        descricao: 'Menu Principal',
        url: '/menu/menu',
        icone: "MenuIcon",
        grupo: ['principal'],
        licenca: ['GERAL']
    };

    const [listaTasks, setListaTasks] = useState<MenuItemType[]>([]);
    const [taskSelecionada, setTaskSelecionada] = useState<MenuItemType | null>(null);
    const [paginasRenderizadas, setPaginasRenderizadas] = useState<any[]>([]);
    const pagesRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const idsAbertos = useRef<Set<string>>(new Set());
    const [usuario, setUsuario] = useState<Record<string, any>>({});
    
    // Estados de dados compartilhados
    const [notaEntrada, setNotaEntrada] = useState<any>(null);
    const [notaSaida, setNotaSaida] = useState<any>(null);
    const [cte, setCte] = useState<any>(null);

    const [configTema, setConfigTema] = useState<NomeTema>('slate');
    const temaAtualizado = useMemo(() => criarTemaCustomizado(configTema), [configTema]);
    const navigate = useNavigate();

    // Verificação de Token
    useEffect(() => {
        tokenService.getToken().then((token) => {
            if (!token) navigate("/login");
            else carregarUsuario();
        }).catch(() => navigate("/login"));
    }, []);

    // Controle de Visibilidade das abas
    useEffect(() => {
        Object.keys(pagesRef.current).forEach((key) => {
            const el = pagesRef.current[key];
            if (el) el.style.display = (key === taskSelecionada?.id) ? 'block' : 'none';
        });
    }, [taskSelecionada, paginasRenderizadas]);

    // Abrir dashboard inicial
    useEffect(() => {
        if (listaTasks.length === 0) abrirPagina(pagainaDashboard);
    }, []);

    const carregarUsuario = async () => {
        const user = await tokenService.getUsuario();
        if (!user) navigate("/login");
        else setUsuario(user);
    };

    const fecharPagina = (item: MenuItemType) => {
        idsAbertos.current.delete(item.id);
        const novaLista = listaTasks.filter(t => t.id !== item.id);
        setPaginasRenderizadas(prev => prev.filter(p => p.props.paginaID !== item.id));
        setListaTasks(novaLista);
        setTaskSelecionada(novaLista.at(-1) || null);
    };

    const abrirPagina = async (item: MenuItemType) => {
        const jaAberta = listaTasks.find(t => t.id === item.id);
        if (!jaAberta && !idsAbertos.current.has(item.id)) {
            idsAbertos.current.add(item.id);
            setListaTasks(prev => [...prev, item]);
            setPaginasRenderizadas(prev => [...prev, buildPagina(item)]);
        }
        setTaskSelecionada(item);
    };

    const getTask = (id: string) => listaTasks.filter(t => t.id === id);

    const buildPagina = (item: MenuItemType) => {
        const PageComponent = React.lazy(() => import("../../paginas/gestao" + item.url));
        return (
            <PageRender
                key={item.id}
                ref={(el: any) => { if (el) pagesRef.current[item.id] = el; }}
                paginaID={item.id}
                registro={item.registro}
            >
                <Suspense fallback={<Box sx={{p: 3}}>Carregando tela...</Box>}>
                    <ConfirmacaoProvider>
                        <PageComponent />
                    </ConfirmacaoProvider>
                </Suspense>
            </PageRender>
        );
    };

    return (
        <EasyBrowserContext.Provider value={{
            listaTasks, abrirPagina, getTask, fecharPagina, taskSelecionada,
            paginasRenderizadas, usuario, notaEntrada, setNotaEntrada,
            notaSaida, setNotaSaida, cte, setCte, configTema, setConfigTema
        }}>
            <ThemeProvider theme={temaAtualizado}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        height: '100vh', 
                        width: '100vw', 
                        overflow: 'hidden',
                        bgcolor: 'background.default'
                    }}>
                        <TaskBar />
                        <PageBody />
                    </Box>
                </LocalizationProvider>
            </ThemeProvider>
        </EasyBrowserContext.Provider>
    );
});

export default Browser;